import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Stack, Alert, Chip, Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { TAXONOMY } from '../constants';
import ModeCard from '../components/ModeCard';
import {
  closeAllocSourceModal,
  setAllocationSource,
  setActiveTab,
  setForwardMode,
  setUploadedAllocationCsv,
  clearUploadedAllocationCsv,
} from '../../../../redux/d2cAllocationSlice';
import {
  useLazyGetAllocationRulesQuery,
  useLazyGetAllocationCsvQuery,
  useGetAllocationMerchantDetailQuery,
} from '../../../../redux/allocationApiSlice';

// Read an uploaded allocation file into 39-column CSV text. .xlsx is converted via
// SheetJS (first sheet); .tsv is re-delimited to CSV; .csv is used verbatim.
async function fileToCsvText(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'xlsx') {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    return XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]]);
  }
  const text = await file.text();
  if (ext === 'tsv') {
    return text
      .split(/\r?\n/)
      .map((line) => line
        .split('\t')
        .map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c))
        .join(','))
      .join('\n');
  }
  return text;
}

function hasExistingAllocation(merchant) {
  if (!merchant) return false;
  return ['KYC_APPROVED', 'Active'].includes(merchant.status) && !!merchant.allocationVersion && merchant.allocationVersion !== '—';
}

// CSV download helper — generates the 39-column blank template
function downloadCsvTemplate() {
  const headers = [
    ...TAXONOMY.csvSchema.fixedColumns,
    ...Array.from({ length: TAXONOMY.csvSchema.priorityColumns }, (_, i) => `Priority${i + 1}`),
    ...TAXONOMY.csvSchema.trailingColumns,
  ];
  // Two example rows
  const fwdRow = new Array(39).fill('');
  fwdRow[0] = 'FORWARD'; fwdRow[1] = 'LOCAL'; fwdRow[2] = '0'; fwdRow[3] = '0.5'; fwdRow[8] = 'PREPAID';
  fwdRow[18] = 'Delhivery_ENT_SURFACE'; fwdRow[19] = 'Ekart_ENT_SURFACE';
  const rvpRow = new Array(39).fill('');
  rvpRow[0] = 'REVERSE'; rvpRow[1] = 'LOCAL'; rvpRow[2] = '0'; rvpRow[3] = '2'; rvpRow[15] = 'NOQC';
  rvpRow[18] = 'Delhivery_ENT_SURFACE';
  const csv = [headers, fwdRow, rvpRow].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Proship_Allocation_Template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function AllocationSourceModal() {
  const dispatch  = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [stagedFile, setStagedFile] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const open = useSelector((s) => s.d2cAllocation.allocSourceModal?.open || false);
  const activeMerchantId = useSelector((s) => s.d2cAllocation.activeMerchantId);

  const { data: merchantRaw } = useGetAllocationMerchantDetailQuery(activeMerchantId, { skip: !activeMerchantId });
  const merchant = merchantRaw?.data ?? merchantRaw ?? null;

  const [getAllocationRules] = useLazyGetAllocationRulesQuery();
  const [getAllocationCsv]   = useLazyGetAllocationCsvQuery();

  const isExisting    = hasExistingAllocation(merchant);
  const group         = isExisting ? 'existing' : 'new';
  const visibleSources = useMemo(
    () => Object.values(TAXONOMY.allocationSources).filter((s) => s.group === group),
    [group],
  );

  const handleClose = () => {
    dispatch(closeAllocSourceModal());
    setSelectedSource(null);
    setStagedFile(null);
  };

  // Sources that show an inline panel instead of proceeding immediately
  const PANEL_SOURCES = ['upload_manual', 'download_edit_upload'];

  const handleSourceClick = (source) => {
    setSelectedSource(source.id);
    setStagedFile(null);
    if (!PANEL_SOURCES.includes(source.id)) proceedWithSource(source.id, null);
  };

  const handleDownloadCurrentCsv = async () => {
    setDownloadingCsv(true);
    try {
      const res = await getAllocationCsv(activeMerchantId).unwrap();
      const csv = res?.data ?? res ?? '';
      if (csv) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `allocation_${activeMerchantId}_current.csv`;
        a.click();
        URL.revokeObjectURL(url);
        enqueueSnackbar('Current allocation CSV downloaded — edit then re-upload below.', { variant: 'info', autoHideDuration: 4000 });
      } else {
        enqueueSnackbar('No published CSV found — download the blank template instead.', { variant: 'warning' });
      }
    } catch {
      enqueueSnackbar('Could not fetch current CSV.', { variant: 'warning' });
    } finally {
      setDownloadingCsv(false);
    }
  };

  const proceedWithSource = async (sourceId, file) => {
    // Panel sources carry an uploaded 39-column allocation CSV. Ingest it, store the raw
    // CSV, and publish it verbatim via the custom-mode csvBlob path (the backend already
    // parses csvBlob for custom publishes), then route to Preview & Publish.
    if (PANEL_SOURCES.includes(sourceId)) {
      if (!file) return;
      let csv;
      try {
        csv = await fileToCsvText(file);
      } catch {
        enqueueSnackbar('Could not read that file — upload a valid 39-column .csv / .xlsx / .tsv.', { variant: 'error' });
        return;
      }
      if (!csv || !csv.trim()) {
        enqueueSnackbar('That file looks empty.', { variant: 'warning' });
        return;
      }
      dispatch(setUploadedAllocationCsv({ name: file.name, csv }));
      dispatch(setForwardMode('custom'));
      dispatch(setAllocationSource(sourceId));
      handleClose();
      enqueueSnackbar(`${file.name} loaded — review on Preview & Publish, then publish.`, { variant: 'success' });
      dispatch(setActiveTab('preview'));
      return;
    }

    // Non-upload sources: drop any stale uploaded CSV so it can't leak into the publish.
    dispatch(clearUploadedAllocationCsv());
    dispatch(setAllocationSource(sourceId));
    handleClose();

    switch (sourceId) {
      case 'hydrate_ui':
        getAllocationRules(activeMerchantId);
        dispatch(setActiveTab('merchant'));
        break;
      case 'scratch':
        dispatch(setActiveTab('merchant'));
        break;
      default:
        dispatch(setActiveTab('merchant'));
    }
  };

  const ingestFile = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'tsv'].includes(ext)) {
      enqueueSnackbar('Please upload a .csv, .xlsx or .tsv file.', { variant: 'warning' });
      return;
    }
    setStagedFile(file);
  };

  const handleFileInput = (e) => { ingestFile(e.target.files?.[0]); e.target.value = ''; };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    ingestFile(e.dataTransfer.files?.[0]);
  };

  const handleConfirmUpload = () => proceedWithSource(selectedSource, stagedFile);

  if (!merchant) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Pick allocation source</Typography>
          <Chip
            size="small"
            label={isExisting ? 'Existing allocation' : 'New allocation'}
            sx={{
              fontSize: 10.5, fontWeight: 600,
              bgcolor: (t) => isExisting ? alpha(t.palette.primary.main, 0.12) : alpha(t.palette.success.main, 0.12),
              color:   (t) => isExisting ? t.palette.primary.dark : t.palette.success.dark,
              border:  (t) => `1px solid ${isExisting ? alpha(t.palette.primary.main, 0.35) : alpha(t.palette.success.main, 0.35)}`,
            }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Editing allocation for{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>{merchant.name}</Box>{' '}·{' '}
          {isExisting
            ? `current version ${merchant.version} (effective ${merchant.effFrom || '—'})`
            : 'no published allocation yet — starting fresh'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {isExisting ? (
          <Alert severity="info" sx={{ mb: 2, py: 0.75 }}>
            This merchant already has an active allocation. Pick how you want to ingest the current setup before editing.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 2, py: 0.75 }}>
            No active allocation exists yet for this merchant. Pick how you want to start.
          </Alert>
        )}

        {/* Source cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
          {visibleSources.map((s) => (
            <ModeCard
              key={s.id}
              icon={s.icon}
              title={s.label}
              description={s.desc}
              tag="Available"
              tagColor="success"
              selected={selectedSource === s.id}
              onClick={() => handleSourceClick(s)}
            />
          ))}
        </Box>

        {/* Upload panel — shown for upload_manual and download_edit_upload */}
        {PANEL_SOURCES.includes(selectedSource) && (
          <Paper
            variant="outlined"
            sx={{ mt: 2, p: 2, borderRadius: 2, borderColor: 'primary.light', bgcolor: (t) => alpha(t.palette.primary.main, 0.02) }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>
                {selectedSource === 'download_edit_upload' ? '📥 Download · Edit · Re-upload' : '📤 Upload your 39-column allocation CSV'}
              </Typography>
              <Stack direction="row" spacing={1}>
                {selectedSource === 'download_edit_upload' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={downloadingCsv}
                    onClick={handleDownloadCurrentCsv}
                    sx={{ fontSize: 11 }}
                  >
                    {downloadingCsv ? 'Downloading…' : '⬇ Download current CSV'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="text"
                  sx={{ fontSize: 11, color: 'text.secondary' }}
                  onClick={downloadCsvTemplate}
                >
                  Download blank template
                </Button>
              </Stack>
            </Stack>

            {stagedFile ? (
              /* File staged — show preview */
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                  border: (t) => `1px solid ${alpha(t.palette.success.main, 0.3)}`,
                }}
              >
                <InsertDriveFileOutlinedIcon sx={{ color: 'success.main', fontSize: 28, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5 }} noWrap>
                    {stagedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(stagedFile.size / 1024).toFixed(1)} KB · ready to load
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<CloseIcon sx={{ fontSize: 13 }} />}
                  onClick={() => setStagedFile(null)}
                  sx={{ fontSize: 11, flexShrink: 0 }}
                >
                  Remove
                </Button>
              </Stack>
            ) : (
              /* Dropzone */
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                sx={{
                  p: 3, textAlign: 'center', border: '2px dashed',
                  borderRadius: 1.5, cursor: 'pointer', transition: 'all .15s',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  bgcolor: dragOver ? (t) => alpha(t.palette.primary.main, 0.06) : 'background.neutral',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.75 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Drag the CSV here, or{' '}
                  <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                    browse to choose
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accepts .csv · .xlsx · .tsv — 39-column Proship allocation format
                </Typography>
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.tsv"
              hidden
              onChange={handleFileInput}
            />
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
        {PANEL_SOURCES.includes(selectedSource) && (
          <Button
            variant="contained"
            disabled={!stagedFile}
            onClick={handleConfirmUpload}
          >
            Continue with this file
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
